import { apiService } from './apiService';

export interface ResumeType {
  value: string;
  label: string;
}

interface ResumeTypesResponse {
  success: boolean;
  data: ResumeType[];
}

interface IndustriesResponse {
  success: boolean;
  data: string[];
}

class ResumeTypesService {
  private cache: Map<string, ResumeType[]> = new Map();
  private industriesCache: string[] | null = null;

  /**
   * Get resume types filtered by industry.
   * Always includes "General Resume" as the first option.
   * 
   * @param industry - Optional industry name to filter by
   * @returns Promise resolving to list of resume types
   */
  async getResumeTypes(industry?: string | null): Promise<ResumeType[]> {
    const cacheKey = industry || 'all';
    
    // Check cache first
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }

    try {
      const url = industry 
        ? `/resumes/types?industry=${encodeURIComponent(industry)}`
        : '/resumes/types';
      
      const response = await apiService.request<ResumeTypesResponse>(url, {
        method: 'GET',
      });

      if (response.success && response.data) {
        // Cache the result
        this.cache.set(cacheKey, response.data);
        return response.data;
      }

      // Fallback to general resume only
      return [{ value: 'general', label: 'General Resume' }];
    } catch (error) {
      console.error('Failed to fetch resume types:', error);
      // Fallback to general resume only
      return [{ value: 'general', label: 'General Resume' }];
    }
  }

  /**
   * Get list of all available industries.
   * 
   * @returns Promise resolving to list of industry names
   */
  async getIndustries(): Promise<string[]> {
    // Check cache first
    if (this.industriesCache) {
      return this.industriesCache;
    }

    // Fallback list of industries (in case API fails)
    const fallbackIndustries = [
      'Technical/Engineering',
      'Business',
      'Healthcare',
      'Medical Professions',
      'Hospitality',
      'Trades & Services',
      'Education',
      'Creative',
      'Legal',
    ];

    try {
      const response = await apiService.request<IndustriesResponse>('/resumes/industries', {
        method: 'GET',
      });

      if (response.success && response.data && Array.isArray(response.data) && response.data.length > 0) {
        // Cache the result
        this.industriesCache = response.data;
        return response.data;
      }

      // Use fallback if API response is invalid
      this.industriesCache = fallbackIndustries;
      return fallbackIndustries;
    } catch (error) {
      // Use fallback on error
      this.industriesCache = fallbackIndustries;
      return fallbackIndustries;
    }
  }

  /**
   * Clear the cache (useful when data might have changed)
   */
  clearCache(): void {
    this.cache.clear();
    this.industriesCache = null;
  }
}

export const resumeTypesService = new ResumeTypesService();

